import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { SessionState } from 'sip.js';
import IncomingCallModal from '@/pages/softphone/IncomingCallModal';
import { resolveSipAccount } from '@/services/sip/sipAccount.fromProfile';
import { useBackendPhonePause } from '@/shared/hooks/useBackendPhonePause';
import { resolveGreetingUploadedBy } from '@/shared/utils/resolve-greeting-uploaded-by';
import { useAppSelector } from '@/store';
import { SipCallContext } from './sipCallContext';
import { useIncomingRingTerminatedListeners } from './useIncomingRingTerminatedListeners';
import { useSipConversationPhase } from './useSipConversationPhase';
import { useSipGlobalDtlsFingerprintListener } from './useSipGlobalDtlsFingerprintListener';
import { useSipProviderActions } from './useSipProviderActions';
import { SipVoiceSessionAcwSync } from './SipVoiceSessionAcwSync';
import { useCallGreetingPreload } from './useCallGreetingPreload';
import { useSipWebSessionLifecycle } from './useSipWebSessionLifecycle';
/**
 * SIP softphone + registration + transfer + supervision. Wiring only — actions live in
 * {@link useSipProviderActions}; session lifecycle in {@link useSipWebSessionLifecycle}.
 */
export function SipCallProvider({ children }) {
    const [sipAudioElement, setSipAudioElement] = useState(null);
    const mgrRef = useRef(null);
    const [sipStatus, setSipStatus] = useState('idle');
    const [sipError, setSipError] = useState(null);
    const [incomingRing, setIncomingRing] = useState(null);
    const incomingRingRef = useRef(incomingRing);
    incomingRingRef.current = incomingRing;
    const [conversationUi, setConversationUi] = useState(null);
    const conversationUiRef = useRef(conversationUi);
    conversationUiRef.current = conversationUi;
    const [sipMediaUiEpoch, setSipMediaUiEpoch] = useState(0);
    const conversationSessionRef = useRef(null);
    const consultSessionRef = useRef(null);
    const warmConsultDialAllowedRef = useRef(false);
    const warmSkipRecoverOnConsultHangupRef = useRef(false);
    const [warmTransfer, setWarmTransfer] = useState({
        phase: 'idle',
        destinationLabel: null,
    });
    const lastOutboundDialRef = useRef('');
    const { subscribeConversationPhaseFnRef, unsubscribeConversationPhaseFnRef } = useSipConversationPhase(setConversationUi, lastOutboundDialRef);
    const { attachRingTerminatedListener, detachRingTerminatedListener, ringTerminatedListenersRef } = useIncomingRingTerminatedListeners(setIncomingRing);
    const { syncAcwFromProfileAfterHangup, isOnBackendAcw } = useBackendPhonePause();
    useSipGlobalDtlsFingerprintListener(setSipError);
    const session = useAppSelector((state) => state.auth.session);
    const greetingUploadedBy = resolveGreetingUploadedBy(session);
    useCallGreetingPreload(greetingUploadedBy || undefined);
    const crmProfile = useAppSelector((state) => state.auth.crmProfile);
    const crmProfileLoaded = useAppSelector((state) => state.auth.crmProfileLoaded);
    const activeBreak = useAppSelector((state) => state.auth.activeBreak);
    const isOnBreak = activeBreak != null;
    const isOnBreakRef = useRef(isOnBreak);
    isOnBreakRef.current = isOnBreak;
    const isOnAcwRef = useRef(isOnBackendAcw);
    isOnAcwRef.current = isOnBackendAcw;
    const sipStatusRef = useRef(sipStatus);
    sipStatusRef.current = sipStatus;
    /** SIP fields only — ACW / pause updates must not tear down the WebSocket session. */
    const account = useMemo(() => resolveSipAccount(crmProfile, { profileLoaded: crmProfileLoaded }), [
        crmProfileLoaded,
        crmProfile?.phoneUser,
        crmProfile?.phonePassword,
        crmProfile?.phoneIp,
        crmProfile?.phonePort,
    ]);
    useSipWebSessionLifecycle({
        accountConfig: account,
        sipAudioElement,
        mgrRef,
        subscribeConversationPhaseFnRef,
        unsubscribeConversationPhaseFnRef,
        conversationSessionRef,
        consultSessionRef,
        warmConsultDialAllowedRef,
        warmSkipRecoverOnConsultHangupRef,
        ringTerminatedListenersRef,
        attachRingTerminatedListener,
        detachRingTerminatedListener,
        setSipStatus,
        setSipError,
        setIncomingRing,
        setWarmTransfer,
        setSipMediaUiEpoch,
        isOnBreakRef,
        isOnAcwRef,
        onVoiceSessionEnded: syncAcwFromProfileAfterHangup,
    });
    const { dial, declineIncoming, answerIncoming, hangupActiveOrDeclineIncoming, toggleMute, toggleHold, transferActiveCall, startWarmTransfer, completeWarmTransfer, cancelWarmTransfer, performSupervisorCall, sendDtmf, } = useSipProviderActions({
        account,
        mgrRef,
        incomingRing,
        conversationUi,
        incomingRingRef,
        sipStatusRef,
        conversationUiRef,
        conversationSessionRef,
        consultSessionRef,
        warmConsultDialAllowedRef,
        warmSkipRecoverOnConsultHangupRef,
        lastOutboundDialRef,
        subscribeConversationPhaseFnRef,
        unsubscribeConversationPhaseFnRef,
        attachRingTerminatedListener,
        detachRingTerminatedListener,
        setSipError,
        setIncomingRing,
        setSipMediaUiEpoch,
        setWarmTransfer,
    });
    /** Incoming toast only while ringing; hide during CRM break or backend ACW. */
    const incomingRingToastVisible = !isOnBreak && !isOnBackendAcw && incomingRing !== null;
    const sm = mgrRef.current;
    const activeEstablishedSession = conversationUi?.sipState === SessionState.Established
        ? conversationSessionRef.current ?? undefined
        : undefined;
    const activeCallMuted = sipMediaUiEpoch >= 0 &&
        !!(sm && activeEstablishedSession && sm.isMuted(activeEstablishedSession));
    const activeCallHeld = sipMediaUiEpoch >= 0 &&
        !!(sm && activeEstablishedSession && sm.isHeld(activeEstablishedSession));
    const value = useMemo(() => ({
        sipStatus,
        sipError,
        isOnBreak,
        incomingRing,
        conversationUi,
        account,
        dial,
        hangupActiveOrDeclineIncoming,
        answerIncoming,
        declineIncoming,
        toggleMute,
        toggleHold,
        transferActiveCall,
        warmTransfer,
        startWarmTransfer,
        completeWarmTransfer,
        cancelWarmTransfer,
        performSupervisorCall,
        sendDtmf,
        activeCallMuted,
        activeCallHeld,
    }), [
        sipStatus,
        sipError,
        isOnBreak,
        incomingRing,
        conversationUi,
        account,
        dial,
        hangupActiveOrDeclineIncoming,
        answerIncoming,
        declineIncoming,
        toggleMute,
        toggleHold,
        transferActiveCall,
        warmTransfer,
        startWarmTransfer,
        completeWarmTransfer,
        cancelWarmTransfer,
        performSupervisorCall,
        sendDtmf,
        activeCallMuted,
        activeCallHeld,
    ]);
    return (_jsxs(SipCallContext.Provider, { value: value, children: [_jsx(SipVoiceSessionAcwSync, {}), _jsx("audio", { ref: setSipAudioElement, playsInline: true, autoPlay: true, controls: false, hidden: true, children: "SIP remote audio" }), children, _jsx(IncomingCallModal, { visible: incomingRingToastVisible, callerLabel: incomingRing?.callerLabel ?? '', calledNumber: incomingRing?.calledNumber, queueName: incomingRing?.queueName, onAnswer: () => answerIncoming(), onDecline: () => hangupActiveOrDeclineIncoming() })] }));
}
